<template>
    <ticket-create-dialog
        v-model="dialog"
        :screen="screen"
        :edited-item.sync="editedItem"
        :categorias="categorias"
        :checklists="checklists"
        :clientes="clientes"
        :use-clientes="true"
        :empresa-value="empresa"
        @update:empresa="empresa = $event"
        :current-user="user"
        :show-date-time-fields="false"
        :tipos-tickets="tiposTickets"
        :selected-service-usa-veiculo="selectedServiceUsaVeiculo"
        :selected-id-empresa="selectedIdEmpresa"
        :selected-service-usa-equipamento="selectedServiceUsaEquipamento"
        :selected-service-usa-kit="selectedServiceUsaKit"
        :vehicle-equipment-pairs.sync="vehicleEquipmentPairs"
        :equipamentos-filtrados-por-empresa="equipamentosFiltradosPorServico"
        :veiculos-filtrados-por-empresa="veiculosFiltradosPorEmpresa"
        :loading-equipamentos="loadingEquipamentos"
        :loading-veiculos="loadingVeiculos"
        :vehicle-equipment-pairs-valid="true"
        :get-equipamento-display-text-by-id="getEquipamentoDisplayTextById"
        :token="token"
        :loading-save.sync="loadingSave"
        :csv-errors-open.sync="csvErrorsOpen"
        @add-pair="addVehiclePair"
        @remove-pair="removeVehiclePair"
        @duplicate-pair="duplicateVehiclePair"
        @save="save()"
        @agency-saved="$emit('created', null)"
    />
</template>

<script>
import apiController from "@/utils/apiController";
import { getScopedClienteIdForCreate } from "@/utils/sessionCliente";
import EventBus from "../../utils/eventBus";
import TicketCreateDialog from "./TicketCreateDialog.vue";

function emptyTicket() {
    return {
        id_ticket: null,
        titulo: '',
        corpo: '',
        id_categoria: null,
        id_status: null,
        id_status_atribuido: null,
        id_status_dispatcher: null,
        id_equipe: null,
        id_equipes: [],
        id_usuario_inclusao: null,
        id_usuario_atribuido: null,
        id_usuarios_atribuidos: [],
        nome_usuario_atribuido: null,
        id_usuarios_visualizadores: [],
        dt_agendamento: null,
        id_checklist_equipamento: null,
        nome_usuario_solicitante: null,
        telefone_solicitante: null,
        id_empresa: null,
        id_cliente: null,
        id_local_empresa: null,
        endereco: null,
        id_tipo_ticket: null,
        id_tipo_solicitacao: null,
        id_familia_atendimento: null,
        id_tipo_atendimento: null,
        id_servico: null,
    };
}

/**
 * Fluxo de criação de ticket da tela unificada (/tickets), disponível em qualquer visão para quem cria tickets.
 * Carrega os cadastros do formulário, monta as combinações veículo/equipamento e cria os tickets.
 * A criação só pede o tipo e o corpo da solicitação: o ticket nasce Pendente (status definido no
 * servidor) e a triagem (família, tipo de atendimento, serviço, grupo solucionador, colaborador,
 * "Compartilhar com") é feita na edição do card.
 * Uso: ref.open(); emite 'created' depois de criar (a página atualiza o quadro, sem abrir o card).
 */
export default {
    name: 'TicketCreateFlow',
    components: { TicketCreateDialog },
    props: {
        user: { type: Object, required: true },
        token: { type: String, default: '' },
        /** Chave de ticket_campos_criacao: 'criados' | 'cliente' */
        screen: { type: String, default: 'criados' },
    },
    data: () => ({
        dialog: false,
        loaded: false,
        loadingSave: false,
        csvErrorsOpen: false,
        editedItem: emptyTicket(),
        categorias: [],
        checklists: [],
        clientes: [],
        tiposTickets: [],
        empresa: null,
        equipamentos: [],
        kits: [],
        tiposEquipamentos: [],
        tiposKits: [],
        equipamentosFiltradosPorEmpresa: [],
        veiculosFiltradosPorEmpresa: [],
        vehicleEquipmentPairs: [],
        loadingEquipamentos: false,
        loadingVeiculos: false,
    }),
    computed: {
        selectedIdEmpresa() {
            if (!this.empresa) return null;
            const cliente = (this.clientes || []).find(c => c.id_cliente == this.empresa);
            return cliente ? cliente.id_empresa : this.empresa;
        },
        /** Sem tipo de atendimento na criação: usa veículo e equipamento, não usa kit. */
        selectedServiceUsaVeiculo() {
            return true;
        },
        selectedServiceUsaEquipamento() {
            return true;
        },
        selectedServiceUsaKit() {
            return false;
        },
        /** Equipamentos/kits do select; .value = id_equipamento. */
        equipamentosFiltradosPorServico() {
            return (this.equipamentosFiltradosPorEmpresa || []).map(e => ({ ...e, value: e.id_equipamento }));
        },
    },
    watch: {
        empresa() {
            const idEmpresa = this.selectedIdEmpresa;
            if (idEmpresa) {
                this.populaEquipamentosPorEmpresa(idEmpresa);
                this.populaVeiculosPorEmpresa(idEmpresa);
                if (!this.vehicleEquipmentPairs || this.vehicleEquipmentPairs.length === 0) {
                    this.vehicleEquipmentPairs = [{ id_veiculo: null, equipamentos: [] }];
                }
            } else {
                this.equipamentosFiltradosPorEmpresa = [];
                this.veiculosFiltradosPorEmpresa = [];
                this.vehicleEquipmentPairs = [];
            }
        },
    },
    mounted() {
        // ticket_campos_criacao define os campos visíveis do formulário (getter por tela no TicketCreateGeralFields).
        this.$store.dispatch('config/fetchTicketCamposCriacao');
    },
    methods: {
        open() {
            if (!this.loaded) {
                this.loaded = true;
                this.initialize();
            }
            this.resetForm();
            this.dialog = true;
        },
        initialize() {
            this.PopulaCategorias();
            this.PopulaChecklists();
            this.PopulaClientes();
            this.PopulaTiposEquipamentos();
            this.PopulaTiposKits();
            this.PopulaTiposTickets();
        },
        resetForm() {
            this.editedItem = emptyTicket();
            this.vehicleEquipmentPairs = [];
            this.equipamentosFiltradosPorEmpresa = [];
            this.veiculosFiltradosPorEmpresa = [];
            // Cliente do usuário (ou do escopo da sessão) vem preenchido.
            const scopedClienteId = getScopedClienteIdForCreate();
            if (scopedClienteId != null) {
                this.empresa = scopedClienteId;
            } else {
                this.empresa = this.user && this.user.id_cliente != null ? this.user.id_cliente : null;
            }
            if (this.empresa) {
                this.vehicleEquipmentPairs = [{ id_veiculo: null, equipamentos: [] }];
            }
        },
        PopulaCategorias() {
            apiController.request(apiController.endpoints.getallCategorias, {
                onSuccess: (response) => {
                    this.categorias = response.data;
                },
            });
        },
        PopulaChecklists() {
            apiController.request(apiController.endpoints.getallChecklistsativos, {
                onSuccess: (response) => {
                    this.checklists = response.data;
                },
            });
        },
        PopulaClientes() {
            apiController.request(apiController.endpoints.getallClientes, {
                onSuccess: (response) => {
                    this.clientes = (response.data || []).filter(c => c.ativo === 1);
                },
            });
        },
        PopulaTiposEquipamentos() {
            apiController.request(apiController.endpoints.getallTiposequipamentos, {
                silent: true,
                onSuccess: (response) => {
                    this.tiposEquipamentos = response.data || [];
                },
                onError: () => {
                    this.tiposEquipamentos = [];
                },
            });
        },
        PopulaTiposKits() {
            apiController.request(apiController.endpoints.getallTiposkits, {
                silent: true,
                onSuccess: (response) => {
                    this.tiposKits = response.data || [];
                },
                onError: () => {
                    this.tiposKits = [];
                },
            });
        },
        PopulaTiposTickets() {
            apiController.request(apiController.endpoints.getallTipostickets, {
                onSuccess: (response) => {
                    this.tiposTickets = response.data;
                },
            });
        },
        /** Separa a seleção de cada par em equipamentos, kits e seleções por tipo ("te_X" / "tk_X"). */
        buildTicketCombinations() {
            return (this.vehicleEquipmentPairs || []).filter(Boolean).map(pair => {
                const equipamentos = [];
                const kits = [];
                const tiposEquipamento = [];
                const tiposKits = [];
                (Array.isArray(pair.equipamentos) ? pair.equipamentos : []).forEach(equipamentoId => {
                    const idStr = String(equipamentoId);
                    if (idStr.startsWith('te_')) {
                        const id = parseInt(idStr.replace('te_', ''), 10);
                        if (!isNaN(id)) tiposEquipamento.push(id);
                        return;
                    }
                    if (idStr.startsWith('tk_')) {
                        const id = parseInt(idStr.replace('tk_', ''), 10);
                        if (!isNaN(id)) tiposKits.push(id);
                        return;
                    }
                    const kit = (this.kits || []).find(k => k.id_kit === equipamentoId);
                    if (kit) {
                        kits.push({ id_kit: kit.id_kit, id_tipo_kit: kit.id_tipo_kit });
                    } else {
                        equipamentos.push(equipamentoId);
                    }
                });
                return {
                    id_equipamentos: equipamentos,
                    id_kits: kits,
                    id_tipos_equipamento: tiposEquipamento,
                    id_tipos_kits: tiposKits,
                    id_veiculos: pair.id_veiculo ? [pair.id_veiculo] : []
                };
            });
        },
        save() {
            // Triagem e atribuição ficam na edição do card; o servidor grava o status Pendente.
            Object.assign(this.editedItem, {
                id_tipo_solicitacao: null,
                id_familia_atendimento: null,
                id_tipo_atendimento: null,
                id_servico: null,
                id_equipe: null,
                id_equipes: [],
                id_usuario_atribuido: null,
                id_usuarios_atribuidos: [],
                id_usuarios_visualizadores: [],
            });

            this.loadingSave = true;
            this.editedItem.id_usuario_inclusao = this.user.id_usuario;

            if (!this.editedItem.titulo || this.editedItem.titulo.trim() === '') {
                this.editedItem.titulo = this.editedItem.corpo && this.editedItem.corpo.trim()
                    ? this.editedItem.corpo.substring(0, 100)
                    : '';
            }

            // Agendamento não existe na criação: só despachante/superadmin define, na edição do ticket.
            const itemToSend = Object.assign({}, this.editedItem, { dt_agendamento: null });
            itemToSend.id_cliente = this.empresa != null ? this.empresa : (this.editedItem.id_cliente != null ? this.editedItem.id_cliente : null);

            // Um ticket por veículo, com seus equipamentos/kits; sem pares cria um único ticket vazio.
            const combinations = this.buildTicketCombinations();
            if (combinations.length > 1) {
                this.createMultipleTickets(itemToSend, combinations);
                return;
            }
            const combination = combinations[0] || {};
            itemToSend.id_equipamentos = combination.id_equipamentos || [];
            itemToSend.id_kits = combination.id_kits || [];
            itemToSend.id_tipos_equipamento = combination.id_tipos_equipamento || [];
            itemToSend.id_tipos_kits = combination.id_tipos_kits || [];
            itemToSend.id_veiculos = combination.id_veiculos || [];

            apiController.request(apiController.endpoints.insertTicket, {
                data: itemToSend,
                onSuccess: (response) => {
                    this.handleSingleSaveResponse(response);
                },
                onError: () => {
                    this.loadingSave = false;
                },
            }).catch(() => {});
        },
        createdTicketId(response) {
            const data = response && response.data;
            if (!data || data.erro) return null;
            return data.id_ticket ?? (data.dados && data.dados.id_ticket) ?? data.insertId ?? null;
        },
        uniqueEquipamentoIdsFromPairs() {
            const ids = [];
            (this.vehicleEquipmentPairs || []).forEach(pair => {
                (Array.isArray(pair.equipamentos) ? pair.equipamentos : []).forEach(v => {
                    const s = String(v);
                    if (!s.startsWith('te_') && !s.startsWith('tk_') && !isNaN(Number(v))) {
                        ids.push(Number(v));
                    }
                });
            });
            return [...new Set(ids)];
        },
        handleSingleSaveResponse(response) {
            this.loadingSave = false;
            if (response.data && response.data.erro) {
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: this.$t('ticket.updateTable.error.insert') + response.data.mensagem
                });
                return;
            }
            const idTicket = this.createdTicketId(response);
            if (idTicket && this.editedItem.id_checklist_equipamento) {
                const equipamentos = this.uniqueEquipamentoIdsFromPairs();
                if (equipamentos.length > 0) {
                    this.autoCreateChecklistResponse(idTicket, equipamentos, this.editedItem.id_checklist_equipamento);
                }
            }
            EventBus.$emit('show-alert', { mensagem: this.$t('ticket.updateTable.success.insert'), tipo: 'success' });
            this.dialog = false;
            this.$root.$emit("atualizarTicketsAbertos");
            // O card do ticket novo não é aberto: a página só atualiza o quadro.
            this.$emit('created', null);
        },
        createMultipleTickets(baseTicket, combinations) {
            const total = combinations.length;
            const idChecklist = baseTicket.id_checklist_equipamento;
            const errors = [];
            let completed = 0;

            EventBus.$emit('show-alert', {
                tipo: 'info',
                mensagem: this.$t('ticket.creatingTickets', { total })
            });

            const finish = () => {
                this.loadingSave = false;
                if (errors.length === total) {
                    EventBus.$emit('show-error', { error: new Error(this.$t('ticket.errorCreatingTickets', { errors: errors.join('; ') })) });
                    return;
                }
                if (errors.length > 0) {
                    EventBus.$emit('show-alert', {
                        tipo: 'warning',
                        mensagem: this.$t('ticket.successCreatingTickets', {
                            total: total - errors.length,
                            totalTotal: total,
                            errors: errors.join('; ')
                        })
                    });
                } else {
                    EventBus.$emit('show-alert', {
                        tipo: 'success',
                        mensagem: this.$t('ticket.successCreatingTicketsAll', { total })
                    });
                }
                this.dialog = false;
                this.$root.$emit("atualizarTicketsAbertos");
                this.$emit('created', null);
            };

            combinations.forEach((combination, index) => {
                const ticketToSend = Object.assign({}, baseTicket, {
                    id_equipamentos: combination.id_equipamentos,
                    id_kits: combination.id_kits || [],
                    id_tipos_equipamento: combination.id_tipos_equipamento || [],
                    id_tipos_kits: combination.id_tipos_kits || [],
                    id_veiculos: combination.id_veiculos,
                });

                apiController.request(apiController.endpoints.insertTicket, {
                    data: ticketToSend,
                    silent: true,
                    onSuccess: (response) => {
                        completed++;
                        if (response.data && response.data.erro) {
                            errors.push(this.$t('ticket.errorCreatingTicket', { index: index + 1, mensagem: response.data.mensagem }));
                        } else if (idChecklist) {
                            const ticketId = this.createdTicketId(response);
                            const equipamentos = [
                                ...(combination.id_equipamentos || []),
                                ...(combination.id_kits || []).map(k => k.id_kit)
                            ];
                            if (ticketId && equipamentos.length > 0) {
                                this.autoCreateChecklistResponse(ticketId, equipamentos, idChecklist);
                            }
                        }
                        if (completed === total) finish();
                    },
                    onError: (e) => {
                        completed++;
                        const errorMsg = (e.response && e.response.data && e.response.data.mensagem) || e.message;
                        errors.push(this.$t('ticket.errorCreatingTicket', { index: index + 1, mensagem: errorMsg }));
                        if (completed === total) finish();
                    },
                }).catch(() => {});
            });
        },
        /** Cria a resposta (vazia) do checklist do serviço para o ticket recém-criado. */
        async autoCreateChecklistResponse(ticketId, equipamentosIds, idChecklist) {
            if (!idChecklist || !ticketId || !equipamentosIds || equipamentosIds.length === 0) return;
            try {
                const checklistResponse = await apiController.request(apiController.endpoints.getChecklistequipamentoporid, {
                    params: { id: idChecklist },
                    silent: true,
                });
                if (!checklistResponse.data || checklistResponse.data.erro) return;
                const checklist = checklistResponse.data;

                if (this.kits.length === 0) {
                    await this.populaKits();
                }

                const valores = (checklist.campos || []).map(campo => ({
                    id_campo: campo.id_campo,
                    valor: campo.tipo === 'Checkbox' ? 'false' : null
                }));
                const equipamentosInfo = equipamentosIds.map(idEquip => {
                    const kit = (this.kits || []).find(k => k.id_kit === idEquip);
                    return kit
                        ? { id_equipamento: idEquip, tipo: 'kit', id_tipo_kit: kit.id_tipo_kit || 1 }
                        : { id_equipamento: idEquip, tipo: 'equipamento', id_tipo_kit: null };
                });
                const firstKit = (this.kits || []).find(k => k.id_kit === equipamentosIds[0]);

                await apiController.request(apiController.endpoints.insertChecklistequipamentoresposta, {
                    data: {
                        id_resposta: null,
                        id_checklist_equipamento: idChecklist,
                        id_equipamentos: equipamentosIds,
                        equipamentos_info: equipamentosInfo,
                        id_tipo_kit: firstKit ? 1 : null,
                        id_funcionario_responsavel: null,
                        observacoes: null,
                        dt_realizacao: this.formatDateLocalForAPI(new Date()),
                        valores,
                        id_ticket: ticketId,
                        id_usuario_alteracao: this.user.id_usuario
                    },
                    silent: true,
                });
            } catch (error) {
                // Falha no checklist não desfaz a criação do ticket.
                console.warn('Failed to auto-create checklist response:', error);
            }
        },
        populaKits() {
            return apiController.request(apiController.endpoints.getallKits, {
                onSuccess: (response) => {
                    this.kits = response.data || [];
                },
            }).catch(() => {});
        },
        populaEquipamentosPorEmpresa(idEmpresa) {
            if (!idEmpresa) {
                this.equipamentosFiltradosPorEmpresa = [];
                return;
            }
            this.loadingEquipamentos = true;

            const filterAndFormat = () => {
                const equipamentos = (this.equipamentos || [])
                    .filter(e => parseInt(e.id_empresa) === parseInt(idEmpresa) && e.ativo === 1)
                    .map(e => ({
                        ...e,
                        tipo: 'equipamento',
                        displayText: e.codigo_qrcode || e.cod_equipamento || e.nome_equipamento || `ID: ${e.id_equipamento}`
                    }));
                // Kits usam id_kit como id_equipamento no select.
                const kits = (this.kits || [])
                    .filter(k => parseInt(k.id_empresa) === parseInt(idEmpresa) && k.ativo === 1)
                    .map(k => ({
                        ...k,
                        id_equipamento: k.id_kit,
                        nome_equipamento: k.nome_kit || k.cod_kit,
                        cod_equipamento: k.cod_kit,
                        id_tipo_kit: k.id_tipo_kit,
                        tipo: 'kit',
                        displayText: `[KIT] ${k.nome_kit || k.cod_kit} (${k.cod_kit})`
                    }));
                this.equipamentosFiltradosPorEmpresa = [...kits, ...equipamentos];
                this.loadingEquipamentos = false;
            };

            const promises = [];
            if (!this.equipamentos || this.equipamentos.length === 0) {
                promises.push(apiController.request(apiController.endpoints.getallEquipamentos, {
                    silent: true,
                    onSuccess: (response) => {
                        this.equipamentos = response.data || [];
                    },
                }));
            }
            if (!this.kits || this.kits.length === 0) {
                promises.push(apiController.request(apiController.endpoints.getallKits, {
                    silent: true,
                    onSuccess: (response) => {
                        this.kits = response.data || [];
                    },
                }));
            }
            Promise.all(promises)
                .then(filterAndFormat)
                .catch((e) => {
                    this.loadingEquipamentos = false;
                    EventBus.$emit('show-error', { error: e });
                });
        },
        populaVeiculosPorEmpresa(idEmpresa) {
            if (!idEmpresa) {
                this.veiculosFiltradosPorEmpresa = [];
                return;
            }
            this.loadingVeiculos = true;
            apiController.request(apiController.endpoints.getVeiculoporempresa, {
                params: { id: idEmpresa },
                onSuccess: (response) => {
                    this.veiculosFiltradosPorEmpresa = (response.data || [])
                        .filter(v => v.ativo === 1 || v.ativo_extenso === 'Sim')
                        .map(v => ({
                            ...v,
                            displayText: v.numero_ordem
                                ? `${v.numero_ordem} (${v.placa || ''})`
                                : (v.placa || `ID: ${v.id_veiculo}`)
                        }));
                    this.loadingVeiculos = false;
                },
                onError: () => {
                    this.loadingVeiculos = false;
                },
            }).catch(() => {});
        },
        getEquipamentoDisplayTextById(id) {
            const idStr = id != null ? String(id) : '';
            if (idStr.startsWith('te_')) {
                const tipoId = parseInt(idStr.replace('te_', ''), 10);
                const t = (this.tiposEquipamentos || []).find(te => parseInt(te.id_tipo_equipamento, 10) === tipoId);
                return t ? (t.nome_tipo_equipamento || ('Tipo ' + tipoId)) : idStr;
            }
            if (idStr.startsWith('tk_')) {
                const tipoId = parseInt(idStr.replace('tk_', ''), 10);
                const t = (this.tiposKits || []).find(tk => parseInt(tk.id_tipo_kit, 10) === tipoId);
                return t ? (t.nome_kit || ('Tipo kit ' + tipoId)) + ' [tipo]' : idStr;
            }
            const item = this.equipamentosFiltradosPorServico.find(e => e.value === id || e.id_equipamento === id)
                || this.equipamentosFiltradosPorEmpresa.find(e => e.id_equipamento === id);
            if (item && item.displayText) return item.displayText;
            const equipamento = this.equipamentos.find(e => e.id_equipamento === id);
            if (equipamento) {
                return equipamento.codigo_qrcode || equipamento.cod_equipamento || equipamento.nome_equipamento || `ID: ${id}`;
            }
            const kit = this.kits.find(k => k.id_kit === id);
            if (kit) {
                return `[KIT] ${kit.nome_kit || kit.cod_kit} (${kit.cod_kit})`;
            }
            return `ID: ${id}`;
        },
        addVehiclePair() {
            this.vehicleEquipmentPairs.push({ id_veiculo: null, equipamentos: [] });
        },
        removeVehiclePair(index) {
            if (this.vehicleEquipmentPairs.length > 1) {
                this.vehicleEquipmentPairs.splice(index, 1);
            }
        },
        duplicateVehiclePair(index) {
            const pair = this.vehicleEquipmentPairs[index];
            this.vehicleEquipmentPairs.splice(index + 1, 0, {
                id_veiculo: pair.id_veiculo != null ? pair.id_veiculo : null,
                equipamentos: Array.isArray(pair.equipamentos) ? [...pair.equipamentos] : []
            });
        },
        formatDateLocalForAPI(date) {
            if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
            const pad = (n) => String(n).padStart(2, '0');
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
        },
    },
};
</script>
